import { translate } from '../i18n/translations';

/**
 * Map Swahili / mixed API roles to canonical English keys used in the app.
 */
export function normalizeRole(role) {
  const r = String(role || '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '_');
  const map = {
    mmiliki: 'owner',
    mmilikii: 'owner',
    mfanyakazi: 'employee',
    mhasibu: 'employee',
    meneja: 'manager',
    manager: 'manager',
    branch_manager: 'manager',
    owner: 'owner',
    business_owner: 'owner',
    mmiliki_wa_biashara: 'owner',
    employee: 'employee',
    admin: 'employee',
    administrator: 'employee',
    cashier: 'employee',
  };
  return map[r] || r || '';
}

export function roleLabel(role, lang = 'en') {
  const n = normalizeRole(role);
  if (n === 'owner') return translate(lang, 'role.owner');
  if (n === 'manager') return translate(lang, 'role.manager');
  if (n === 'employee') return translate(lang, 'role.employee');
  return n ? n.charAt(0).toUpperCase() + n.slice(1) : translate(lang, 'role.employee');
}

export function roleLabelEn(role) {
  return roleLabel(role, 'en');
}

export function isOwnerRole(role) {
  return normalizeRole(role) === 'owner';
}

export function isManagerRole(role) {
  return normalizeRole(role) === 'manager';
}

/** Owner or branch manager — may approve stock/damage and view expenses. */
export function canModerateBranch(role) {
  const n = normalizeRole(role);
  return n === 'owner' || n === 'manager';
}

/** Revenue, profit, and other money figures on dashboards — not shown to staff. */
export function canViewFinancials(role) {
  return canModerateBranch(role);
}

/** Settings hub and owner configuration — owner only. */
export function canAccessSettings(role) {
  return isOwnerRole(role);
}

export function isBranchTeamRole(role) {
  const r = normalizeRole(role);
  return r === 'employee' || r === 'manager';
}

/** Full name is a generic Swahili account label (e.g. "Mmiliki", "Mmiliki User"). */
const PLACEHOLDER_FULL_NAME = /^(m{2,}ilik(i|ii)?|mfanyakazi|mhasibu)(\s+user)?$/i;

function isPlaceholderFirstName(token) {
  if (!token) return true;
  const t = token.trim().toLowerCase();
  if (/^(mfanyakazi|mhasibu)$/i.test(t)) return true;
  // Email local-parts like "mmiliki" (owner)
  return /^m{2,}ilik(i|ii)?$/i.test(t);
}

function capitalizeWord(s) {
  if (!s) return '';
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

/**
 * First name for greetings / header. Skips Swahili placeholder names; falls back to email local-part.
 */
export function firstDisplayNameFromUser(user) {
  const rawName = user?.name != null ? String(user.name).trim() : '';
  if (rawName) {
    const first = rawName.split(/\s+/)[0];
    const fullPlaceholder = PLACEHOLDER_FULL_NAME.test(rawName);
    const firstBad = isPlaceholderFirstName(first);
    if (first && !fullPlaceholder && !firstBad) {
      return capitalizeWord(first);
    }
  }
  if (user?.email) {
    const local = user.email.split('@')[0] || '';
    const cleaned = local.replace(/[._]+/g, ' ').trim();
    if (!cleaned) return roleFallbackFirst(user);
    const token = cleaned.split(/\s+/)[0].toLowerCase();
    if (isPlaceholderFirstName(token) || PLACEHOLDER_FULL_NAME.test(cleaned)) {
      return roleFallbackFirst(user);
    }
    return capitalizeWord(cleaned.split(/\s+/)[0]);
  }
  return roleFallbackFirst(user);
}

function roleFallbackFirst(user) {
  const r = roleLabelEn(user?.role);
  return r !== 'User' ? r : 'there';
}
